import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus, Clock, Lightbulb } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { Placeholder } from "@/components/Placeholder";

const Callout = ({ text }) => (
  <aside data-testid="article-pull-quote" className="my-10 flex gap-4 rounded-r-xl border-l-4 border-brand bg-brand-soft p-6">
    <Lightbulb size={20} className="mt-0.5 shrink-0 text-brand" />
    <p className="font-display text-lg font-semibold leading-snug text-ink md:text-xl">{text}</p>
  </aside>
);

const Article = ({ blocks }) => {
  let p = 0;
  return (
    <article data-testid="service-article" className="max-w-none">
      {blocks.map((block, i) =>
        block.type === "quote" ? (
          <Callout key={i} text={block.text} />
        ) : (
          <p key={i} data-testid={`article-paragraph-${++p}`} className={`mb-6 leading-relaxed text-slate-700 ${p === 1 ? "text-lg font-medium text-ink md:text-xl" : "text-base md:text-lg"}`}>
            {block.text}
          </p>
        )
      )}
    </article>
  );
};

const ListBlock = ({ title, items, icon: Icon, iconCls, testId }) => (
  <div data-testid={testId} className="border-b border-slate-200 px-6 py-5">
    <p className="label-xs">{title}</p>
    <ul className="mt-3 space-y-2.5">
      {items.map((it) => (
        <li key={it} className="flex gap-2.5 text-sm leading-snug text-slate-700">
          <Icon size={16} className={`mt-0.5 shrink-0 ${iconCls}`} />
          {it}
        </li>
      ))}
    </ul>
  </div>
);

const AtAGlance = ({ service }) => (
  <aside data-testid="service-glance-box" className="card overflow-hidden lg:sticky lg:top-24">
    <div className="border-b border-slate-200 bg-cream px-6 py-4">
      <p className="font-display text-sm font-bold">At a glance</p>
    </div>
    <ListBlock title="What's included" items={service.included} icon={Check} iconCls="text-forest" testId="service-included" />
    <ListBlock title="Not included" items={service.notIncluded} icon={Minus} iconCls="text-slate-400" testId="service-not-included" />
    <div className="border-b border-slate-200 px-6 py-5">
      <p className="label-xs">Typical delivery</p>
      <p data-testid="service-timeframe" className="mt-2 inline-flex items-center gap-2 rounded-full bg-cream px-3 py-1.5 text-sm text-slate-700">
        <Clock size={14} className="text-brand" /> <Placeholder field={service.timeframe} />
      </p>
    </div>
    <div className="px-6 py-5">
      <p className="label-xs">Pricing</p>
      <p data-testid="service-pricing" className="mt-2 font-display text-xl font-bold">{service.pricing}</p>
      <p className="mt-2 text-xs leading-relaxed text-slate-500">{service.pricingNote}</p>
      <Link to={`/contact?service=${service.slug}`} data-testid="service-quote-link" className="btn-primary mt-5 w-full">
        Request a Quote <ArrowRight size={16} />
      </Link>
    </div>
  </aside>
);

export const ServiceArticlePage = ({ service, blocks, overline, lede, seoDescription }) => (
  <div data-testid={`${service.slug}-page`}>
    <Seo title={service.title} description={seoDescription} />
    <PageHeader overline={overline} title={service.title} lede={lede} image={service.image} imageAlt={service.alt} />
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20">
      <div className="grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-8">
          <Article blocks={blocks} />
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-4">
          <AtAGlance service={service} />
        </Reveal>
      </div>
    </section>
    <CTABand
      title={`Need ${service.title.toLowerCase()}? Let's scope it.`}
      text="Tell us about your business and we'll reply with a written quotation — scope, timeframe and price, before any work begins."
    />
  </div>
);
